import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Optional, Output, SimpleChanges } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { DropdownCoordinatorService } from '../../../services/dropdown-coordinator.service';

export interface SearchableItem {
  [key: string]: any;
}

@Component({
  standalone: false,
  selector: 'app-searchable-selection',
  templateUrl: './searchable-selection.component.html',
  styleUrls: ['./searchable-selection.component.scss']
})
export class SearchableSelectionComponent implements OnInit, OnChanges, OnDestroy {
  @Input() items: SearchableItem[] = [];
  @Input() displayProperty: string = 'name';
  @Input() displaySuffixProperty: string = ''; // When set, display as "displayValue (suffixValue)", e.g. name (code)
  @Input() selectedDisplayProperty: string = ''; // Property to use for selected value display (if different from displayProperty)
  @Input() valueProperty: string = 'id'; // Property to use as value
  @Input() searchProperties: string[] = ['name'];
  @Input() placeholder: string = 'Search and select...';
  @Input() disabled: boolean = false;
  @Input() showValueProperty: string = '';
  @Input() selectedValue: any = null; // Selected value (e.g., product id)
  /**
   * If true, component will not filter locally. Parent should update `items` based on `searchTermChange`.
   */
  @Input() serverSideSearch: boolean = false;
  @Input() searchDebounceMs: number = 300;
  @Input() allowAddNew: boolean = false;
  @Input() addNewLabel: string = '+ Add new';
  /** When set, participates in dropdown coordination: opening this dropdown closes others in the same group. */
  @Input() dropdownId: string = '';
  @Output() itemSelected = new EventEmitter<SearchableItem>();
  @Output() valueSelected = new EventEmitter<any>(); // Emits the value (e.g., id)
  @Output() searchTermChange = new EventEmitter<string>();
  @Output() addNewRequested = new EventEmitter<string>();

  searchTerm: string = '';
  showDropdown: boolean = false;
  filteredItems: SearchableItem[] = [];
  selectedItem: SearchableItem | null = null;

  private searchTerm$ = new Subject<string>();
  private searchSub?: Subscription;
  private destroy$ = new Subject<void>();

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    @Optional() private dropdownCoordinator?: DropdownCoordinatorService
  ) {}

  ngOnInit(): void {
    this.filteredItems = [...this.items];
    this.updateSelectedItem();

    this.searchSub = this.searchTerm$
      .pipe(debounceTime(this.searchDebounceMs), distinctUntilChanged())
      .subscribe((term) => this.searchTermChange.emit(term));

    if (this.dropdownCoordinator && this.dropdownId) {
      this.dropdownCoordinator.openDropdown
        .pipe(
          filter((id) => id !== this.dropdownId),
          takeUntil(this.destroy$)
        )
        .subscribe(() => this.closeDropdown());
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items']) {
      this.filterItems();
      this.updateSelectedItem();
    }
    if (changes['selectedValue']) {
      this.updateSelectedItem();
    }
    if (changes['searchDebounceMs'] && this.searchSub) {
      this.searchSub.unsubscribe();
      this.searchSub = this.searchTerm$
        .pipe(debounceTime(this.searchDebounceMs), distinctUntilChanged())
        .subscribe((term) => this.searchTermChange.emit(term));
    }
  }

  onSearchChange(): void {
    if (this.selectedItem) return; // Ignore when selection locked
    if (this.searchTerm.trim()) {
      this.showDropdown = true;
    }
    if (this.serverSideSearch) {
      // Parent will provide items matching the search term; we just emit the term.
      this.filteredItems = [...this.items];
      this.searchTerm$.next(this.searchTerm);
    } else {
      this.filterItems();
    }
  }

  onFocus(): void {
    if (this.selectedItem) {
      return; // When value is selected, don't open dropdown - only removable via X
    }
    if (this.dropdownCoordinator && this.dropdownId) {
      this.dropdownCoordinator.notifyDropdownOpened(this.dropdownId);
    }
    this.showDropdown = true;
    this.filterItems();
    if (this.serverSideSearch) {
      this.searchTerm$.next(this.searchTerm);
    }
  }

  onKeydown(event: KeyboardEvent): void {
    if (this.selectedItem && event.key !== 'Escape') {
      event.preventDefault(); // Block typing when selection exists - only Escape or X clears
    }
    if (this.selectedItem && event.key === 'Escape') {
      this.clearSelection();
    }
  }

  closeDropdown(): void {
    this.showDropdown = false;
  }

  onBlur(): void {
    // Use setTimeout to allow click events on dropdown items to fire before closing
    setTimeout(() => {
      // Check if focus moved to an element inside the component
      const activeElement = document.activeElement;
      if (activeElement && !this.elementRef.nativeElement.contains(activeElement)) {
        this.showDropdown = false;
      }
    }, 150);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node | null;
    if (!target) return;
    // Check if click is inside the dropdown or the component
    const clickedInside = this.elementRef.nativeElement.contains(target);
    if (!clickedInside) {
      this.showDropdown = false;
    }
  }

  filterItems(): void {
    if (this.serverSideSearch) {
      this.filteredItems = [...this.items];
      return;
    }
    if (!this.searchTerm.trim()) {
      this.filteredItems = [...this.items];
      return;
    }

    const searchLower = this.searchTerm.toLowerCase();
    this.filteredItems = this.items.filter(item => {
      return this.searchProperties.some(prop => {
        const value = this.getNestedProperty(item, prop);
        return value && value.toString().toLowerCase().includes(searchLower);
      });
    });
  }

  getNestedProperty(obj: any, path: string): any {
    return path.split('.').reduce((current, prop) => current?.[prop], obj);
  }

  selectItem(item: SearchableItem): void {
    if (this.disabled) return;
    this.selectedItem = item;
    this.searchTerm = this.getSelectedDisplayValue(item);
    const value = item[this.valueProperty];
    this.itemSelected.emit(item);
    this.valueSelected.emit(value);
    this.showDropdown = false;
  }

  requestAddNew(): void {
    if (this.disabled) return;
    const term = (this.searchTerm || '').trim();
    this.addNewRequested.emit(term);
    this.showDropdown = false;
  }

  clearSearch(): void {
    this.searchTerm = '';
    if (this.selectedItem) {
      this.searchTerm = this.getSelectedDisplayValue(this.selectedItem);
    }
    this.showDropdown = false;
    this.filterItems();
  }

  clearSelection(): void {
    this.selectedItem = null;
    this.searchTerm = '';
    this.showDropdown = false;
    this.itemSelected.emit(null as any);
    this.valueSelected.emit(null);
    this.filterItems();
  }

  updateSelectedItem(): void {
    if (this.items.length === 0) {
      this.selectedItem = null;
      // In server-side search mode we must NOT wipe the user's typed text if no selection exists yet.
      if (this.selectedValue !== null && this.selectedValue !== undefined && this.selectedValue !== '') {
        this.searchTerm = String(this.selectedValue);
      } else if (!this.serverSideSearch) {
        this.searchTerm = '';
      }
      return;
    }

    if (this.selectedValue !== null && this.selectedValue !== undefined && this.selectedValue !== '') {
      const selVal = this.selectedValue;
      this.selectedItem =
        this.items.find(item => this.getNestedProperty(item, this.valueProperty) == selVal) || null;
      this.searchTerm = this.selectedItem ? this.getSelectedDisplayValue(this.selectedItem) : String(this.selectedValue);
      return;
    }

    // No selection => clear input text
    this.selectedItem = null;
    if (!this.serverSideSearch) {
      this.searchTerm = '';
    }
  }

  ngOnDestroy(): void {
    this.searchSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  getDisplayValue(item: SearchableItem): string {
    const main = this.getNestedProperty(item, this.displayProperty) || '';
    if (!this.displaySuffixProperty) return main;
    const suffix = this.getNestedProperty(item, this.displaySuffixProperty);
    return suffix ? `${main} (${suffix})` : main;
  }

  getSelectedDisplayValue(item: SearchableItem): string {
    if (this.selectedDisplayProperty) {
      const selectedDisplay = this.getNestedProperty(item, this.selectedDisplayProperty);
      if (selectedDisplay) return selectedDisplay;
    }
    return this.getDisplayValue(item);
  }

  getShowValue(item: SearchableItem): string {
    if (!this.showValueProperty) return '';
    return this.getNestedProperty(item, this.showValueProperty) || '';
  }

  formatValue(item: SearchableItem): string {
    const value = this.getShowValue(item);
    if (!value) return '';
    
    // If it's a number, format as currency
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      return new Intl.NumberFormat('bs-BA', {
        style: 'currency',
        currency: 'BAM'
      }).format(numValue);
    }
    
    return value;
  }

  trackByValue(index: number, item: SearchableItem): any {
    return item[this.valueProperty] ?? index;
  }
}

